import type { CdkDragDrop } from '@angular/cdk/drag-drop';
import { CdkDrag, CdkDropList } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';

import type moment from 'moment-timezone';
import { Observable, of, switchMap } from 'rxjs';

import { TimeService } from '@core/common-legacy';
import { injectTranslate, TranslatePipe } from '@core/i18n-legacy';
import { TellentMembersDataFacade } from '@core/tellent-logic';
import {
  resolveAssignmentFailureReason,
  TellentPositionsDataFacade,
} from '@core/tellent-positions-logic';
import {
  MemberPositionHistory,
  PositionAssignment,
  PositionHistoryDialogData,
  PositionHistoryEntryPayload,
  TellentPosition,
} from '@core/tellent-positions-types';
import { PositionHistoryEditCardComponent, PositionRowForm } from '@core/tellent-positions-ui';
import { resolveMemberFullName } from '@core/tellent-util';
import {
  AvatarComponent,
  BadgeComponent,
  ButtonComponent,
  DialogBodyComponent,
  DialogComponentI,
  DialogHeaderComponent,
  DialogRef,
  DialogService,
  EmptyStateComponent,
  IconComponent,
  NotificationsController,
  Select2Item,
  SpinnerComponent,
} from '@core/user-interface-legacy';

import {
  isRowComplete,
  isRowEmpty,
  latestStartDate,
  makePositionRow,
  positionRowValue,
  rowEntry,
  rowsWithChangedStartDate,
  startDatesByRow,
} from './position-history-form';
import { buildMemberPositionHistory } from './position-history.mapper';

const START_DATE_CHANGED_NOTICE_MS = 2000;

@Component({
  selector: 'tlc-position-history-dialog',
  templateUrl: './position-history-dialog.component.html',
  styleUrls: ['./position-history-dialog.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    AvatarComponent,
    BadgeComponent,
    ButtonComponent,
    CdkDrag,
    CdkDropList,
    DialogBodyComponent,
    DialogHeaderComponent,
    EmptyStateComponent,
    IconComponent,
    PositionHistoryEditCardComponent,
    SpinnerComponent,
    TranslatePipe,
  ],
})
export class PositionHistoryDialogComponent implements DialogComponentI<
  PositionHistoryDialogData,
  void
> {
  private readonly dialogRef = inject(DialogRef);
  private readonly timeService = inject(TimeService);
  private readonly positionsFacade = inject(TellentPositionsDataFacade);
  private readonly membersFacade = inject(TellentMembersDataFacade);
  private readonly notificationsController = inject(NotificationsController);
  private readonly t = injectTranslate();
  private readonly dialogService = inject(DialogService);

  // Fetched with the soft-deleted ones and kept out of the positions store, which every other
  // consumer reads expecting live positions only.
  private readonly positions = toSignal<TellentPosition[], TellentPosition[]>(
    this.positionsFacade.fetchPositionsIncludingDeleted(),
    { initialValue: [] },
  );

  private readonly form = new FormGroup({
    scheduled: new FormArray<PositionRowForm>([]),
    current: makePositionRow(),
    previous: new FormArray<PositionRowForm>([]),
  });

  private readonly formValue = toSignal(this.form.valueChanges);

  private readonly assignments = computed<PositionAssignment[]>(() => {
    const history = this.history();
    if (!history) return [];
    const current = history.current ? [history.current] : [];
    return [...history.previous, ...current, ...history.scheduled];
  });

  protected readonly history = signal<MemberPositionHistory | null>(null);
  protected readonly isLoading = signal(true);
  protected readonly isSaving = signal(false);
  protected readonly mode = signal<'view' | 'edit'>('view');

  protected readonly isEdit = computed(() => this.mode() === 'edit');

  protected readonly isEmpty = computed(() => this.assignments().length === 0);

  protected readonly memberName = computed(() => {
    const member = this.memberData?.();
    return member ? resolveMemberFullName(member) : '';
  });
  protected readonly memberAvatarUrl = computed(() => this.memberData?.()?.avatarThumbUrl ?? null);

  protected readonly scheduled = this.form.controls.scheduled;
  protected readonly currentRow = this.form.controls.current;
  protected readonly previous = this.form.controls.previous;

  protected readonly canEdit = signal(true);

  // Reordering rewrites the dates of every row the dragged one displaced, so the rows carrying a
  // rewritten date are flagged by reference — the same reference the template tracks them by.
  protected readonly startDateChangedRows = signal<ReadonlySet<PositionRowForm>>(new Set());

  protected readonly canSave = computed(() => {
    this.formValue();
    return this.rows().every(row => isRowComplete(row) || isRowEmpty(row));
  });

  // A position that no longer exists is only offered for previous entries: recording where somebody
  // used to work stays meaningful after the position is gone, moving them there does not.
  protected readonly positionItems = computed<Array<Select2Item<string>>>(() =>
    this.positions()
      .filter(position => !position.deletedAt)
      .map(position => ({ value: position.guid, label: position.name })),
  );

  protected readonly previousPositionItems = computed<Array<Select2Item<string>>>(() => {
    const deletedLabel = this.t('tlc.positions.position_history_dialog.deleted_position_badge');
    const deletedBadge: Select2Item['badge'] = {
      icon: 'delete',
      color: 'default',
      label: deletedLabel,
      tooltip: deletedLabel,
    };

    return this.positions().map(position => ({
      value: position.guid,
      label: position.name,
      ...(position.deletedAt ? { badge: deletedBadge } : {}),
    }));
  });

  private memberGuid = '';
  private memberData?: ReturnType<TellentMembersDataFacade['teamMemberById']>;
  private startDateChangedTimeout?: ReturnType<typeof setTimeout>;

  public setDialogData(data: PositionHistoryDialogData): void {
    this.memberGuid = data.memberGuid;
    this.memberData = this.membersFacade.teamMemberById(data.memberGuid);
    // The dialog is opened with a guid alone, and its callers do not necessarily have the members
    // collection loaded — the positions details dialog, for one, never loads it.
    if (!this.memberData()) {
      this.membersFacade.fetchOne(data.memberGuid);
    }
    this.canEdit.set(data.canEdit ?? true);
    this.dialogRef.containerInstance.dialogConfig = {
      ...this.dialogRef.containerInstance.dialogConfig,
      closeGuard: () => (this.isEdit() && !this.isSaving() ? this.confirmLeave() : of(true)),
    };
    this.loadHistory();
  }

  // Keeps each section's dates inside its own status, so the backend never has to re-derive a row
  // into a status the user did not ask for — which is what the ordering rules reject.
  //
  // Stable references, and bound: the date picker is re-created every time its overlay opens, so
  // reading the form directly is enough to keep the bounds current.
  protected readonly previousDateBlackout = (date: moment.Moment): boolean => {
    const currentStart = this.currentRow.controls.startDate.value;
    return !!currentStart && date.format('YYYY-MM-DD') >= currentStart;
  };

  protected readonly currentDateBlackout = (date: moment.Moment): boolean => {
    const latestPrevious = latestStartDate(this.previous.controls);
    return !!latestPrevious && date.format('YYYY-MM-DD') <= latestPrevious;
  };

  protected startedLabel(assignment: PositionAssignment): string {
    return this.t('tlc.positions.position_history_dialog.started', {
      date: this.timeService.format({ input: assignment.startDate, pattern: 'D MMM YYYY' }),
    });
  }

  protected enterEdit(): void {
    const history = this.history();
    if (!history) return;

    this.resetForm(history);
    this.mode.set('edit');
  }

  protected cancelEdit(): void {
    this.mode.set('view');
  }

  protected save(): void {
    if (this.isSaving() || !this.canSave()) return;

    this.isSaving.set(true);

    const entries = this.historyEntries();
    const patch$: Observable<unknown> = this.hasHistoryChanges(entries)
      ? this.positionsFacade.updateMemberPositionHistory(this.memberGuid, entries)
      : of(null);

    patch$
      .pipe(switchMap(() => this.positionsFacade.fetchMemberPositionHistory(this.memberGuid)))
      .subscribe({
        next: assignments => {
          this.history.set(buildMemberPositionHistory(assignments));
          this.positionsFacade.fetchPositions();
          this.isSaving.set(false);
          // Back to view mode rather than closing, so the saved history stays on screen. The next
          // `enterEdit` rebuilds the form from `history`, which now carries the ids the save
          // created — without that, saving twice would create the same rows again.
          this.mode.set('view');
          this.notificationsController.success(
            this.t('tlc.positions.position_history_dialog.notification_success'),
          );
        },
        error: (error: unknown) => {
          this.notificationsController.error(
            resolveAssignmentFailureReason(error) ??
              this.t('tlc.positions.position_history_dialog.notification_error'),
          );
          // The write is one transaction, so a rejection left the history untouched. Stay in edit
          // mode with the form as submitted, so the offending row can be corrected.
          this.isSaving.set(false);
        },
      });
  }

  protected scheduleChange(): void {
    this.scheduled.push(makePositionRow());
  }

  protected removeScheduled(index: number): void {
    this.scheduled.removeAt(index);
  }

  protected addPrevious(): void {
    this.previous.push(makePositionRow());
  }

  protected removePrevious(index: number): void {
    this.previous.removeAt(index);
  }

  protected isRowDraggable(row: PositionRowForm): boolean {
    return isRowComplete(row);
  }

  // Order is not stored — the history is read back sorted by start date. Reordering therefore moves
  // the rows between the dates rather than the dates with the rows: every slot keeps the date it
  // already had, so the descending sequence this section renders survives the save, and the payload
  // is a plain date rewrite per id. Rows still missing a date are skipped, so displacing one can
  // neither swallow nor blank out a date somebody filled in.
  protected reorderPrevious(event: CdkDragDrop<unknown>): void {
    const { previousIndex, currentIndex } = event;
    if (previousIndex === currentIndex) return;

    const dates = this.previous.controls
      .map(row => row.controls.startDate.value)
      .filter((date): date is string => !!date);

    const datesBefore = startDatesByRow(this.previous.controls);

    const row = this.previous.at(previousIndex);
    this.previous.removeAt(previousIndex);
    this.previous.insert(currentIndex, row);

    this.previous.controls
      .filter(control => !!control.controls.startDate.value)
      .forEach((control, index) => control.controls.startDate.setValue(dates[index]));

    this.flagStartDateChanged(rowsWithChangedStartDate(this.previous.controls, datesBefore));
  }

  // Shown briefly and then faded out: the notice explains a date the user did not type, so it has
  // to be noticed at the moment of the drop, not linger over the row it describes.
  private flagStartDateChanged(rows: PositionRowForm[]): void {
    clearTimeout(this.startDateChangedTimeout);
    this.startDateChangedRows.set(new Set(rows));
    if (!rows.length) return;

    this.startDateChangedTimeout = setTimeout(
      () => this.startDateChangedRows.set(new Set()),
      START_DATE_CHANGED_NOTICE_MS,
    );
  }

  private loadHistory(): void {
    this.isLoading.set(true);
    this.positionsFacade.fetchMemberPositionHistory(this.memberGuid).subscribe({
      next: assignments => {
        this.history.set(buildMemberPositionHistory(assignments));
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
        this.notificationsController.error(
          this.t('tlc.positions.position_history_dialog.load_error'),
        );
        this.dialogRef.close();
      },
    });
  }

  private resetForm(history: MemberPositionHistory): void {
    // The rows below are rebuilt, so the flagged references are about to go stale.
    this.flagStartDateChanged([]);

    this.currentRow.reset(positionRowValue(history.current));

    this.scheduled.clear();
    for (const assignment of history.scheduled) {
      this.scheduled.push(makePositionRow(assignment));
    }

    this.previous.clear();
    for (const assignment of history.previous) {
      this.previous.push(makePositionRow(assignment));
    }
  }

  private rows(): PositionRowForm[] {
    return [this.currentRow, ...this.scheduled.controls, ...this.previous.controls];
  }

  // The whole history as it should look afterwards: rows carrying an id are rewritten, rows without
  // one are created, and the ones left out are the deletions.
  private historyEntries(): PositionHistoryEntryPayload[] {
    return this.rows().filter(isRowComplete).map(rowEntry);
  }

  private hasHistoryChanges(entries: PositionHistoryEntryPayload[]): boolean {
    const storedById = new Map(this.assignments().map(assignment => [assignment.id, assignment]));

    if (entries.length !== storedById.size) return true;

    return entries.some(entry => {
      // A row with no id is a create, so there is nothing stored to compare it against.
      if (entry.id === null) return true;

      const stored = storedById.get(entry.id);
      return (
        !stored ||
        stored.positionGuid !== entry.positionGuid ||
        stored.startDate !== entry.startDate
      );
    });
  }

  private confirmLeave(): Observable<boolean> {
    return this.dialogService.error({
      title: this.t('tlc.positions.position_history_dialog.leave_confirmation.title'),
      message: this.t('tlc.positions.position_history_dialog.leave_confirmation.message'),
      agreeLabel: this.t('tlc.positions.position_history_dialog.leave_confirmation.confirm'),
      isCancelable: true,
    });
  }
}
